/**
 * Tabs — wrapper around @radix-ui/react-tabs.
 *
 * Radix imports live here (per frontend-conventions no-restricted-imports rule);
 * consumers import these wrappers and get the project's tab styling + className
 * merge baked in. Same Radix API surface — Root / List / Trigger / Content —
 * just with default classes applied.
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import styles from './Tabs.module.css';

function cx(...classes: (string | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

const Root = forwardRef<
  ElementRef<typeof RadixTabs.Root>,
  ComponentPropsWithoutRef<typeof RadixTabs.Root>
>(function TabsRoot({ className, ...rest }, ref) {
  return <RadixTabs.Root ref={ref} className={cx(styles.root, className)} {...rest} />;
});

const List = forwardRef<
  ElementRef<typeof RadixTabs.List>,
  ComponentPropsWithoutRef<typeof RadixTabs.List>
>(function TabsList({ className, ...rest }, ref) {
  return <RadixTabs.List ref={ref} className={cx(styles.list, className)} {...rest} />;
});

const Trigger = forwardRef<
  ElementRef<typeof RadixTabs.Trigger>,
  ComponentPropsWithoutRef<typeof RadixTabs.Trigger>
>(function TabsTrigger({ className, ...rest }, ref) {
  return <RadixTabs.Trigger ref={ref} className={cx(styles.trigger, className)} {...rest} />;
});

const Content = forwardRef<
  ElementRef<typeof RadixTabs.Content>,
  ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(function TabsContent({ className, ...rest }, ref) {
  return <RadixTabs.Content ref={ref} className={cx(styles.content, className)} {...rest} />;
});

export const Tabs = { Root, List, Trigger, Content };
